import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../components/layout/DashboardLayout';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Cell,
} from 'recharts';
import {
  TrendingUp, Users, Send, MessageSquare, Trophy, Target,
  ArrowUpRight, BarChart3, Loader2, Calendar, Flame,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Analytics = () => {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [series, setSeries] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [niches, setNiches] = useState([]);
  const [windowDays, setWindowDays] = useState(30);

  useEffect(() => { fetchAll(windowDays); }, [windowDays]);

  const fetchAll = async (days) => {
    setLoading(true);
    try {
      const [o, t, f, n] = await Promise.all([
        axios.get(`${API_URL}/analytics/overview?days=${days}`),
        axios.get(`${API_URL}/analytics/timeseries?days=${days <= 14 ? 14 : 30}`),
        axios.get(`${API_URL}/analytics/funnel`),
        axios.get(`${API_URL}/analytics/by-niche`),
      ]);
      setOverview(o.data);
      setSeries(t.data.series || []);
      setFunnel(f.data);
      setNiches(n.data.rows || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !overview) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
        </div>
      </DashboardLayout>
    );
  }

  const o = overview.outreach || {};
  const l = overview.leads || {};

  return (
    <DashboardLayout>
      <div className="px-4 sm:px-6 lg:px-10 py-6 lg:py-8 space-y-6 lg:space-y-8 max-w-[1500px] mx-auto" data-testid="analytics-content">

        {/* Hero */}
        <section className="card-base p-6 sm:p-8 relative overflow-hidden">
          <div className="absolute right-0 top-0 w-72 h-72 bg-gradient-to-br from-violet-200/40 to-blue-200/40 rounded-full blur-3xl -mr-20 -mt-20" />
          <div className="relative grid sm:grid-cols-[1fr_auto] gap-4 items-end">
            <div>
              <p className="text-[11px] font-bold tracking-[0.18em] text-violet-700 uppercase mb-2">Performance</p>
              <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">Analytics</h1>
              <p className="text-slate-600 mt-2 max-w-xl">
                Real outcomes: replies, conversions, and the channels actually working for you.
              </p>
            </div>
            <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg">
              {[7, 14, 30, 90].map(d => (
                <button
                  key={d}
                  onClick={() => setWindowDays(d)}
                  data-testid={`window-${d}`}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                    windowDays === d ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-900'
                  }`}
                >
                  {d}d
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* KPI ROW */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi testid="kpi-leads"     icon={Users}          label="Potential Clients" value={l.total || 0}
               hint={`+${l.recent || 0} in last ${overview.window_days}d`} accent="violet" />
          <Kpi testid="kpi-msgs"      icon={Send}           label="Opportunities Created" value={o.messages_total || 0}
               hint={`${o.messages_recent || 0} this window`} accent="blue" />
          <Kpi testid="kpi-reply"     icon={MessageSquare}  label="Reply Rate" value={`${o.reply_rate || 0}%`}
               hint={`${l.by_status?.replied || 0} replied`} accent="emerald" />
          <Kpi testid="kpi-close"     icon={Trophy}         label="Close Rate" value={`${o.close_rate || 0}%`}
               hint={`${l.by_status?.closed || 0} closed`} accent="amber" />
        </section>

        {/* Time series + Funnel */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Time series */}
          <div className="lg:col-span-2 card-base p-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2.5">
                <span className="icon-halo"><TrendingUp className="w-4 h-4 text-violet-700" strokeWidth={2.5} /></span>
                <div>
                  <h2 className="text-base font-heading font-bold text-slate-900">Activity over time</h2>
                  <p className="text-xs text-slate-500">Leads, outreach, replies — daily</p>
                </div>
              </div>
            </div>
            <div className="h-72 min-h-[260px]">
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}>
                <AreaChart data={series} margin={{ top: 10, right: 10, bottom: 0, left: -22 }}>
                  <defs>
                    <linearGradient id="leadGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#7C3AED" stopOpacity={0.4}/>
                      <stop offset="100%" stopColor="#7C3AED" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="msgGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2563EB" stopOpacity={0.35}/>
                      <stop offset="100%" stopColor="#2563EB" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="replyGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10B981" stopOpacity={0.35}/>
                      <stop offset="100%" stopColor="#10B981" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="label" stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, fontSize: 12 }} />
                  <Area type="monotone" dataKey="leads"    stroke="#7C3AED" strokeWidth={2} fill="url(#leadGrad)"  name="Leads added" />
                  <Area type="monotone" dataKey="messages" stroke="#2563EB" strokeWidth={2} fill="url(#msgGrad)"   name="Outreach sent" />
                  <Area type="monotone" dataKey="replies"  stroke="#10B981" strokeWidth={2} fill="url(#replyGrad)" name="Replies" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-slate-500">
              <Legend dot="#7C3AED" label="Leads added" />
              <Legend dot="#2563EB" label="Outreach sent" />
              <Legend dot="#10B981" label="Replies" />
            </div>
          </div>

          {/* Funnel */}
          <div className="card-base p-5 sm:p-6" data-testid="funnel-card">
            <div className="flex items-center gap-2.5 mb-5">
              <span className="icon-halo"><Target className="w-4 h-4 text-violet-700" strokeWidth={2.5} /></span>
              <div>
                <h2 className="text-base font-heading font-bold text-slate-900">Acquisition Funnel</h2>
                <p className="text-xs text-slate-500">All-time conversion</p>
              </div>
            </div>

            {funnel?.stages?.length > 0 ? (
              <div className="space-y-2.5">
                {funnel.stages.map((s, i) => {
                  const colors = ['from-violet-500 to-violet-600', 'from-blue-500 to-blue-600', 'from-emerald-500 to-emerald-600', 'from-amber-500 to-orange-500'];
                  return (
                    <div key={s.label}>
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-xs font-semibold text-slate-700">{s.label}</p>
                        <p className="text-xs font-bold text-slate-900 tabular-nums">{s.value}</p>
                      </div>
                      <div className="relative h-8 rounded-lg bg-slate-100 overflow-hidden">
                        <div
                          className={`absolute inset-y-0 left-0 bg-gradient-to-r ${colors[i]} transition-all duration-500`}
                          style={{ width: `${Math.max(s.pct_of_top, 4)}%` }}
                        />
                        <div className="absolute inset-0 flex items-center justify-end pr-3">
                          <span className="text-[11px] font-bold text-slate-700 tabular-nums">{s.pct_of_top}%</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}

            {funnel?.step_rates && (
              <div className="mt-5 pt-5 border-t border-slate-100 grid grid-cols-3 gap-2 text-center">
                <FunnelStat label="L→C" value={`${funnel.step_rates.lead_to_contact}%`} />
                <FunnelStat label="C→R" value={`${funnel.step_rates.contact_to_reply}%`} />
                <FunnelStat label="R→💰" value={`${funnel.step_rates.reply_to_close}%`} />
              </div>
            )}
          </div>
        </section>

        {/* Niche performance */}
        <section className="card-base p-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2.5">
              <span className="icon-halo"><BarChart3 className="w-4 h-4 text-violet-700" strokeWidth={2.5} /></span>
              <div>
                <h2 className="text-base font-heading font-bold text-slate-900">Performance by niche</h2>
                <p className="text-xs text-slate-500">Where your replies actually come from</p>
              </div>
            </div>
          </div>

          {niches.length === 0 ? (
            <div className="empty-state">
              <span className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-blue-500 flex items-center justify-center mb-3 shadow-glow">
                <BarChart3 className="w-5 h-5 text-white" />
              </span>
              <p className="font-heading font-bold text-slate-900">No niches yet</p>
              <p className="text-sm text-slate-500 max-w-sm mt-1.5">Save leads across niches and your conversion data will appear here.</p>
              <Link to="/search" className="mt-4">
                <button className="btn-primary"><Users className="w-4 h-4" /> Find clients</button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={niches.slice(0, 8)} layout="vertical" margin={{ top: 6, right: 18, bottom: 6, left: -10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                    <XAxis type="number" stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis type="category" dataKey="niche" stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} width={100} />
                    <Tooltip contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, fontSize: 12 }} />
                    <Bar dataKey="total" name="Leads" radius={[6, 6, 6, 6]}>
                      {niches.slice(0, 8).map((_, i) => (
                        <Cell key={i} fill={['#7C3AED', '#2563EB', '#10B981', '#F59E0B', '#EC4899', '#06B6D4', '#8B5CF6', '#EF4444'][i]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-[11px] font-bold tracking-[0.12em] text-slate-500 uppercase">
                      <th className="text-left py-2 pr-4">Niche</th>
                      <th className="text-right py-2 pr-2">Leads</th>
                      <th className="text-right py-2 pr-2">Replied</th>
                      <th className="text-right py-2">Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {niches.map((n, i) => (
                      <tr key={i} className="border-b border-slate-100" data-testid={`niche-row-${i}`}>
                        <td className="py-2.5 pr-4 font-semibold text-slate-900 truncate max-w-[180px]">{n.niche}</td>
                        <td className="py-2.5 pr-2 text-right tabular-nums">{n.total}</td>
                        <td className="py-2.5 pr-2 text-right tabular-nums text-emerald-700">{n.replied}</td>
                        <td className="py-2.5 text-right">
                          <span className={`badge ${
                            n.reply_rate >= 20 ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200' :
                            n.reply_rate >= 10 ? 'bg-amber-50 text-amber-700 ring-1 ring-amber-200' :
                            'bg-slate-100 text-slate-600 ring-1 ring-slate-200'
                          }`}>{n.reply_rate}%</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>

        {/* Active Challenge mini-block */}
        {overview.active_challenge && (
          <section className="card-gradient-border" data-testid="analytics-challenge-card">
            <div className="flex flex-col sm:flex-row gap-4 p-5 sm:p-6 items-start sm:items-center">
              <span className="icon-halo flex-shrink-0">
                <Flame className="w-4 h-4 text-violet-700" strokeWidth={2.5} />
              </span>
              <div className="flex-1">
                <p className="font-heading font-bold text-slate-900">
                  Day {overview.active_challenge.current_day}/{overview.active_challenge.duration_days || 90} · {overview.active_challenge.completion_pct}% complete
                </p>
                <p className="text-sm text-slate-600 mt-1">
                  Outreach progress this challenge: {overview.active_challenge.outreach_done_count}/{overview.active_challenge.outreach_due} ({overview.active_challenge.outreach_pct}%) · {overview.active_challenge.streak} 🔥 streak.
                </p>
              </div>
              <Link to="/challenge">
                <button className="btn-secondary" data-testid="analytics-open-challenge-button"><Calendar className="w-4 h-4" /> Open challenge</button>
              </Link>
            </div>
          </section>
        )}
      </div>
    </DashboardLayout>
  );
};

const Kpi = ({ icon: Icon, label, value, hint, accent, testid }) => {
  const accents = {
    violet:  'from-violet-500 to-violet-600',
    blue:    'from-blue-500 to-blue-600',
    emerald: 'from-emerald-500 to-emerald-600',
    amber:   'from-amber-500 to-orange-500',
  };
  return (
    <div className="card-base card-hover p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-bold tracking-[0.16em] text-slate-500 uppercase">{label}</p>
        <span className={`w-9 h-9 rounded-xl bg-gradient-to-br ${accents[accent]} flex items-center justify-center text-white shadow-sm`}>
          <Icon className="w-4 h-4" strokeWidth={2.5} />
        </span>
      </div>
      <p className="text-3xl font-heading font-extrabold text-slate-900 mt-3 tracking-tight tabular-nums">{value}</p>
      <p className="text-xs text-slate-500 mt-1">{hint}</p>
    </div>
  );
};

const Legend = ({ dot, label }) => (
  <span className="inline-flex items-center gap-1.5">
    <span className="w-2 h-2 rounded-full" style={{ background: dot }} />
    {label}
  </span>
);

const FunnelStat = ({ label, value }) => (
  <div>
    <p className="font-heading text-lg font-extrabold text-slate-900 tabular-nums">{value}</p>
    <p className="text-[10px] text-slate-500 font-bold tracking-[0.14em] uppercase mt-0.5">{label}</p>
  </div>
);

export default Analytics;
